import { drawSignature, withRights } from "./pngMeta";
import { hearts } from "./hearts";
import { zoneLabel } from "./sections";
import { colourFlow } from "./curate";
import { imageSrc } from "./api";
import { drawLockup, fit, loadImg } from "./shareCard";

const W = 1080;
const H = 1350;
const FONTS = ['400 56px "Parisienne"', '500 20px "Cormorant Garamond"', '500 30px "Caveat"', '500 50px "Caveat"', 'italic 400 34px "Cormorant Garamond"'];

export function journeyFeelings(paintings, viewed) {
  const seen = new Set();
  const out = [];
  viewed.forEach((slug) => {
    const p = paintings.find((q) => q.slug === slug);
    if (!p) return;
    const k = p.emotion.trim().toLowerCase();
    if (seen.has(k)) return;
    seen.add(k);
    out.push({ emotion: p.emotion, painting: p });
  });
  paintings.forEach((p) => {
    if (!hearts.mine.has(p.id) || out.some((f) => f.painting.id === p.id)) return;
    out.push({ emotion: p.emotion, painting: p });
  });
  return colourFlow(out.map((f) => ({ ...f.painting, __f: f }))).map((p) => p.__f);
}

const rgba = (hex, lighten, a) => {
  const n = parseInt((hex || "#BA68C8").slice(1), 16);
  const c = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => Math.round(v + (255 - v) * lighten));
  return `rgba(${c.join(",")},${a})`;
};

function layout(n, area) {
  let best = null;
  for (let c = 1; c <= n; c++) {
    const rows = Math.ceil(n / c);
    const cw = area.w / c;
    const ch = area.h / rows;
    const r = Math.min(210, Math.min(cw, ch) * 0.43);
    if (!best || r > best.r) best = { c, rows, cw, ch, r };
  }
  const j = Math.min(best.cw, best.ch) * 0.03;
  return Array.from({ length: n }, (_, i) => {
    const row = Math.floor(i / best.c);
    const col = i % best.c;
    const inRow = row < best.rows - 1 ? best.c : n - best.c * (best.rows - 1);
    const x0 = area.x + (area.w - inRow * best.cw) / 2;
    return { x: x0 + (col + 0.5) * best.cw + Math.sin(i * 2.1) * j, y: area.y + (row + 0.5) * best.ch + Math.cos(i * 1.7) * j, r: best.r };
  });
}

function wash(ctx) {
  ctx.fillStyle = "#FBF6FA";
  ctx.fillRect(0, 0, W, H);
  [[180, 200, 560, "#E9DDF7"], [930, 280, 500, "#F9DDE8"], [880, 1060, 600, "#DDEBF8"], [180, 1160, 540, "#FBF0D9"], [540, 640, 440, "#F3E6FA"]].forEach(([x, y, r, c]) => {
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, rgba(c, 0, 0.85));
    g.addColorStop(1, rgba(c, 0, 0));
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
  });
  for (let i = 0; i < 2600; i++) {
    ctx.fillStyle = `rgba(120,90,140,${Math.random() * 0.04})`;
    ctx.fillRect(Math.random() * W, Math.random() * H, 2, 2);
  }
}

function bubble(ctx, b, img, color) {
  const { x, y, r } = b;
  for (let k = 0; k < 4; k++) {
    const ox = Math.cos(k * 1.9) * r * 0.04;
    const oy = Math.sin(k * 2.3) * r * 0.04;
    const rr = r * (0.9 + k * 0.022);
    const g = ctx.createRadialGradient(x + ox - rr * 0.3, y + oy - rr * 0.3, rr * 0.1, x + ox, y + oy, rr);
    g.addColorStop(0, "rgba(255,255,255,0.2)");
    g.addColorStop(0.72, rgba(color, 0.55, 0.16));
    g.addColorStop(1, rgba(color, 0.45, 0));
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(x + ox, y + oy, rr, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.lineWidth = Math.max(2, r * 0.03);
  ctx.strokeStyle = rgba(color, 0.35, 0.32);
  ctx.beginPath();
  ctx.arc(x, y, r * 0.95, 0, Math.PI * 2);
  ctx.stroke();
  ctx.lineWidth = Math.max(1.5, r * 0.018);
  ctx.strokeStyle = "rgba(255,255,255,0.9)";
  ctx.beginPath();
  ctx.arc(x, y, r * 0.88, Math.PI * 1.05, Math.PI * 1.55);
  ctx.stroke();
  const gr = r * 0.52;
  if (img) {
    ctx.save();
    ctx.beginPath();
    ctx.arc(x, y, gr, 0, Math.PI * 2);
    ctx.clip();
    const ar = img.naturalWidth / img.naturalHeight;
    const w = ar > 1 ? gr * 2 * ar : gr * 2;
    const h = ar > 1 ? gr * 2 : (gr * 2) / ar;
    ctx.drawImage(img, x - w / 2, y - h / 2, w, h);
    ctx.restore();
  }
  ctx.lineWidth = Math.max(3, r * 0.04);
  ctx.strokeStyle = "#FFFFFF";
  ctx.beginPath();
  ctx.arc(x, y, gr, 0, Math.PI * 2);
  ctx.stroke();
  ctx.fillStyle = "rgba(255,255,255,0.6)";
  ctx.beginPath();
  ctx.ellipse(x - r * 0.5, y - r * 0.48, r * 0.13, r * 0.06, -0.7, 0, Math.PI * 2);
  ctx.fill();
}

const hit = (a, b) => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

export async function makeJourneyCard(feelings, heading = "the feelings I walked through") {
  await Promise.all(FONTS.map((f) => document.fonts.load(f))).catch(() => {});
  const imgs = await Promise.all(feelings.map((f) => loadImg(imageSrc(f.painting.thumb_url || f.painting.image_url)).catch(() => null)));
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  wash(ctx);

  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.font = 'italic 400 34px "Cormorant Garamond"';
  ctx.fillStyle = "#5C4A72";
  ctx.fillText(heading, W / 2, 84);

  const n = feelings.length;
  const cols = n > 18 ? 4 : n > 12 ? 3 : n > 6 ? 2 : 1;
  const fs = cols === 4 ? 32 : cols === 3 ? 38 : cols === 2 ? 44 : 50;
  const lh = Math.round(fs * 1.18);
  const perCol = Math.ceil(n / cols);
  const lockTop = H - 178;
  const poemTop = lockTop - 30 - perCol * lh;
  const colW = cols === 1 ? 800 : cols === 2 ? 420 : cols === 3 ? 300 : 225;
  const colX = cols === 1 ? [W / 2] : cols === 2 ? [W / 2 - 230, W / 2 + 230] : cols === 3 ? [W / 2 - 320, W / 2, W / 2 + 320] : [W / 2 - 360, W / 2 - 120, W / 2 + 120, W / 2 + 360];
  const area = { x: 70, y: 124, w: W - 140, h: poemTop - 40 - 124 };
  const spots = layout(n, area);
  spots.forEach((b, i) => bubble(ctx, b, imgs[i], feelings[i].painting.palette?.[0]));

  ctx.font = `500 ${fs}px "Caveat"`;
  ctx.fillStyle = "#4A2D55";
  const words = feelings.map((f, i) => {
    const c = Math.floor(i / perCol);
    const y = poemTop + ((i % perCol) + 0.8) * lh;
    const text = fit(ctx, hearts.mine.has(f.painting.id) ? `♥ ${f.emotion}` : f.emotion, colW);
    ctx.fillText(text, colX[c], y);
    const z = zoneLabel(f.painting.section);
    if (z) {
      ctx.save();
      ctx.font = `italic 500 ${Math.max(22, Math.round(fs * 0.5))}px "Cormorant Garamond"`;
      ctx.fillStyle = "#5B3A85";
      ctx.fillText(fit(ctx, z, colW), colX[c], y + fs * 0.42);
      ctx.restore();
    }
    const w = ctx.measureText(text).width;
    return { text, x: colX[c] - w / 2, y: y - fs * 0.8, w, h: fs };
  });
  drawLockup(ctx, W, H);

  let overlaps = 0;
  spots.forEach((a, i) => spots.slice(i + 1).forEach((b) => Math.hypot(a.x - b.x, a.y - b.y) < a.r + b.r && overlaps++));
  spots.forEach((b) => b.y + b.r > poemTop - 4 && overlaps++);
  words.forEach((a, i) => words.slice(i + 1).forEach((b) => hit(a, b) && overlaps++));
  words.forEach((w) => w.y + w.h > lockTop && overlaps++);

  drawSignature(ctx, W - 48, H - 40, 34);
  const blob = await withRights(await new Promise((r) => canvas.toBlob(r, "image/png")), "My journey - Feeling Heart - Rainbow by the Rain");
  const info = { width: W, height: H, count: n, words: feelings.map((f) => f.emotion), bubbles: spots.map((b) => ({ x: Math.round(b.x), y: Math.round(b.y), r: Math.round(b.r) })), poemTop, lockTop, overlaps, glimpses: imgs.filter(Boolean).length };
  window.__afaeJourney = { last: info };
  return { blob, info };
}
